/* eslint-disable no-await-in-loop */
import type { Identifiable } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { Mutex } from 'async-mutex';
import type { Opaque } from 'type-fest';
import type { Broker, Factory, RoomBroker } from './types.js';

/**
 * Create a reference-counted resource broker.
 *
 * @param factory - Resource factory.
 * @returns Resource broker.
 */
export const makeBroker = <T extends Identifiable, U>(factory: Factory<T, U>): Broker<T, U> => {
  const resources = new Map<T['id'], T>();
  const listeners = new Set<() => void>();
  const mutex = new Mutex();

  const acquire = (id: T_Maybe<T['id']>): T_Maybe<T> => (id ? resources.get(id) : undefined);
  const notify = (): void => listeners.forEach((listener) => listener());
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const create = async (id: T['id'], ...args: U[]): Promise<T> =>
    mutex.runExclusive(async () => {
      let resource = resources.get(id);

      if (!resource) {
        resource = await factory.create(id, ...args);
        resources.set(id, resource);
        notify();
      }

      return resource;
    });

  const destroy = async (id: T['id']): Promise<void> =>
    mutex.runExclusive(async () => {
      const resource = resources.get(id);

      if (!resource) {
        return;
      }

      try {
        await factory.destroy(resource);
      } finally {
        // Failed teardown must not leave a dead resource available for reacquisition.
        resources.delete(id);
        notify();
      }
    });

  return {
    acquire,
    create,
    destroy,
    subscribe,

    [Symbol.iterator]: () => resources.values()
  };
};

/**
 * Create a reference-counted resource broker.
 *
 * @param factory - Resource factory.
 * @returns Resource broker.
 */
export const makeRoomBroker = <T extends Identifiable, U>(
  factory: Factory<T, U>
): RoomBroker<T, U> => {
  const broker = makeBroker<T, U>(factory);

  // Room retirement queues immediately, even while its factory is still pending.
  const roomMutex = new Mutex();
  const destroyRoom = (owned: T['id'][] | Promise<Record<string, T>>): Promise<void> =>
    roomMutex.runExclusive(async () => {
      const ids = Array.isArray(owned) ? owned : (Object.keys(await owned) as T['id'][]);
      await Promise.all(ids.map((id) => broker.destroy(id)));
    });

  const createRoom = (
    agents: Record<Opaque<string, '__agentId'>, Omit<Agent, 'status'>>,
    ...args: U[]
  ): Promise<Record<string, T>> => {
    let complete!: (resources: Record<string, T>) => void;
    let fail!: (error: unknown) => void;
    const creation = new Promise<Record<string, T>>((resolve, reject) => {
      complete = resolve;
      fail = reject;
    });
    void roomMutex
      .runExclusive(async () => {
        const ids = Object.values(agents).map((agent) => agent.id);

        const results: Record<string, T> = {};

        const promises = ids.map(async (id) => {
          const [firstArgs, ...rest] = args;

          const nextArgs = <U[]>[
            {
              ...firstArgs,
              name: agents[id]?.name,
              embedded: agents[id]?.embedded,
              modelId: agents[id]?.modelId,
              brandId: agents[id]?.brandId
            },
            ...rest
          ];

          // `nextArgs` is forwarded verbatim, so the CALLER owns whatever credential
          // accessor the factory needs for later refreshes — the agent broker
          // threads `tokenGenerator`, the LiveKit broker threads `getAuthToken`.
          // This call site mints nothing itself, so it has no token to refresh.
          // Per rule: code-comments-required (ai.knowledge/global).
          const proxy = await broker.create(id, ...nextArgs);

          results[id] = proxy;
        });

        try {
          await Promise.all(promises);
          complete(results);
        } catch (error) {
          // Report the original factory failure immediately, but retain room ownership
          // until every successful/late sibling is retired before replacement admission.
          fail(error);
          await Promise.allSettled(promises);
          await Promise.allSettled(Object.keys(results).map((id) => broker.destroy(id as T['id'])));
        }
      })
      .catch(fail);
    return creation;
  };

  return {
    ...broker,
    createRoom,
    destroyRoom
  };
};
